/** Canonical, presentation-independent report document preparation. */

import type { JSONContent } from "@tiptap/core";
import type { StructuredReportResponse } from "../hooks/useReportQuery";
import { requireReadyWording } from "./reportWording";
import { GPT_IMPRESSION_DISCLOSURE, IMPRESSION_UNAVAILABLE_NOTICE } from "./impressionDisclosure";
import { streamImpression, type ImpressionFinding, type ImpressionItem } from "./impressionStream";
import {
  buildImpressionSection,
  buildReportDoc,
  extractFindingsForImpression,
  hasLegacyTemplateImpression,
} from "./reportUtils";

export interface SavedReportForDownload {
  final_report_html?: string | null;
  final_report_text?: string | null;
  structured_editor_state?: unknown;
  is_report_signed?: boolean;
}

export interface ReportDownloadOptions {
  accessionNumber?: string | null;
  /** "throw" (default): a report whose Impression cannot be generated rejects,
   * so nothing downloads. "note": it resolves with an explicit
   * Impression-unavailable notice printed in the PDF instead — for bulk
   * exports, where the other reports should still download. Never silent:
   * the notice is visible in the document and flagged on the result. */
  onImpressionFailure?: "throw" | "note";
}

export type GeneratedReportForDownload = Pick<StructuredReportResponse, "sections" | "meta">;

export interface ReportPreparationDependencies {
  streamImpression: typeof streamImpression;
}

export type ReportBlockEmphasis = "standard" | "ai-positive";

export interface ReportTextItem {
  text: string;
  emphasis: ReportBlockEmphasis;
}

export type ReportBlock =
  | { kind: "labeled"; label: string; items: ReportTextItem[] }
  | { kind: "numbered"; number: string; text: string; emphasis: ReportBlockEmphasis }
  | { kind: "disclosure"; text: string }
  | { kind: "notice"; text: string }
  | { kind: "paragraph"; text: string; emphasis: ReportBlockEmphasis };

export interface ReportDocumentSection {
  title: string;
  blocks: ReportBlock[];
}

/** Content, ordering, and semantic emphasis consumed by every export format. */
export interface ReportDocument {
  sections: ReportDocumentSection[];
}

export interface DownloadableReport {
  filename: string;
  signed: boolean;
  document: ReportDocument;
  /** Stable, display-safe case identifier used in the PDF header. */
  caseIdentifier?: string;
  /** Present for Worklist exports, including null when the source has no accession. */
  accessionNumber?: string | null;
  /** True when the document carries the Impression-unavailable notice instead
   * of a generated Impression (onImpressionFailure: "note" only). */
  impressionUnavailable?: boolean;
}

type EditorNode = {
  type?: string;
  text?: string;
  attrs?: Record<string, unknown>;
  content?: EditorNode[];
};

function plainText(node: EditorNode | undefined): string {
  if (!node) return "";
  if (node.type === "text") return node.text ?? "";
  if (node.type === "field") return typeof node.attrs?.text === "string" ? node.attrs.text : "";
  if (node.type === "hardBreak") return "\n";
  return (node.content ?? []).map(plainText).join("");
}

function paragraphs(node: EditorNode): string[] {
  return (node.content ?? [])
    .filter((child) => child.type === "paragraph")
    .map((child) => plainText(child).trim())
    .filter(Boolean);
}

function containsAiFinding(node: EditorNode): boolean {
  if (node.type === "field" && node.attrs?.kind === "ai-finding") return true;
  return (node.content ?? []).some(containsAiFinding);
}

function paragraphReportItems(node: EditorNode): ReportTextItem[] {
  const content = node.content ?? [];
  const fields = content.filter(
    (child) =>
      child.type === "field" && typeof child.attrs?.text === "string" && child.attrs.text.trim(),
  );
  const hasNonFieldText = content.some(
    (child) => child.type !== "field" && plainText(child).trim(),
  );

  if (fields.length && !hasNonFieldText) {
    return fields.map((field) => ({
      text: String(field.attrs?.text).trim(),
      emphasis: field.attrs?.kind === "ai-finding" ? "ai-positive" : "standard",
    }));
  }

  const text = plainText(node).trim();
  return text ? [{ text, emphasis: containsAiFinding(node) ? "ai-positive" : "standard" }] : [];
}

export function reportDocumentToText(document: ReportDocument): string {
  return document.sections
    .filter((section) => section.blocks.length > 0)
    .map((section) => {
      const lines = section.blocks.map((block) => {
        if (block.kind === "labeled") {
          return `${block.label}:\n${block.items.map((item) => item.text).join("\n")}`;
        }
        if (block.kind === "numbered") return `${block.number}. ${block.text}`;
        return block.text;
      });
      return `${section.title}:\n\n${lines.join("\n")}`;
    })
    .join("\n\n");
}

export function editorStateToReportDocument(state: unknown): ReportDocument | null {
  const root = editorStateJson(state) as EditorNode | null;
  if (!root || !Array.isArray(root.content)) return null;

  const findings: ReportBlock[] = [];
  const impressions: ReportBlock[] = [];
  let hasGptImpression = false;

  const visit = (node: EditorNode) => {
    if (node.type === "organSubsection") {
      const label = typeof node.attrs?.label === "string" ? node.attrs.label : "FINDINGS";
      const items = (node.content ?? [])
        .filter((child) => child.type === "paragraph")
        .flatMap(paragraphReportItems);
      if (items.length) {
        findings.push({
          kind: "labeled",
          label,
          items,
        });
      }
      return;
    }
    if (node.type === "impressionItem") {
      const text = paragraphs(node).join(" ");
      if (text) {
        if (node.attrs?.provenance === "ai-detected") hasGptImpression = true;
        impressions.push({
          kind: "numbered",
          number: String(impressions.length + 1),
          text,
          emphasis: node.attrs?.provenance === "ai-detected" ? "ai-positive" : "standard",
        });
      }
      return;
    }
    (node.content ?? []).forEach(visit);
  };
  visit(root);

  const sections: ReportDocumentSection[] = [];
  if (findings.length) sections.push({ title: "FINDINGS", blocks: findings });
  if (impressions.length) {
    sections.push({
      title: "IMPRESSION",
      blocks: hasGptImpression
        ? [{ kind: "disclosure", text: GPT_IMPRESSION_DISCLOSURE }, ...impressions]
        : impressions,
    });
  }
  return sections.length ? { sections } : null;
}

/** Plain-text compatibility view derived from the canonical editor document. */
export function editorStateToReportText(state: unknown): string | null {
  const document = editorStateToReportDocument(state);
  return document ? reportDocumentToText(document) : null;
}

function replaceImpressionSection(doc: JSONContent, items: ImpressionItem[]): JSONContent {
  let replaced = false;
  const content = (doc.content ?? []).map((node) => {
    if (node.type !== "impressionSection") return node;
    replaced = true;
    return buildImpressionSection(items, false);
  });
  if (!replaced) throw new Error("The report draft has no Impression section.");
  return { ...doc, content };
}

function hasImpression(document: ReportDocument): boolean {
  return document.sections.some(
    (section) =>
      section.title.toUpperCase() === "IMPRESSION" &&
      section.blocks.some((block) => block.kind !== "disclosure"),
  );
}

function editorStateJson(state: unknown): JSONContent | null {
  if (typeof state === "string") {
    try {
      return JSON.parse(state) as JSONContent;
    } catch {
      return null;
    }
  }
  return state && typeof state === "object" ? (state as JSONContent) : null;
}

/** One bounded retry so a single transient GPT failure does not blank a
 * report out of a large export. A deliberate abort is never retried. */
async function generateImpression(
  generate: typeof streamImpression,
  sources: ReadonlyArray<ImpressionFinding>,
): Promise<ImpressionItem[]> {
  try {
    return await generate(sources);
  } catch (error) {
    if (error instanceof DOMException && error.name === "AbortError") throw error;
    return await generate(sources);
  }
}

async function replaceLegacyTemplateImpression(
  state: unknown,
  generate: typeof streamImpression,
): Promise<ReportDocument> {
  const editorState = editorStateJson(state);
  if (!editorState) throw new Error("The saved draft is not a valid report document.");
  const sources = extractFindingsForImpression(editorState);
  const migrated = replaceImpressionSection(
    editorState,
    await generateImpression(generate, sources),
  );
  const document = editorStateToReportDocument(migrated);
  if (!document || !hasImpression(document)) {
    throw new Error("A complete report with Impression could not be prepared.");
  }
  return document;
}

function generatedEditorState(report: GeneratedReportForDownload): JSONContent {
  if (!report.sections) throw new Error("The report service did not return report sections.");
  const findings = Object.entries(report.sections).map(([kind, section]) => ({
    kind,
    label: section.label?.trim() || kind.replaceAll(/[-_]+/g, " ").toUpperCase(),
    default: section.defaultText ?? "",
  }));
  return buildReportDoc({ findings }, report.sections);
}

async function generatedReportToDocument(
  report: GeneratedReportForDownload,
  generate: typeof streamImpression,
): Promise<ReportDocument> {
  let editorState = generatedEditorState(report);
  const impressionSources = extractFindingsForImpression(editorState);
  editorState = replaceImpressionSection(
    editorState,
    await generateImpression(generate, impressionSources),
  );

  const document = editorStateToReportDocument(editorState);
  if (!document || !hasImpression(document)) {
    throw new Error("A complete report with Impression could not be prepared.");
  }
  return document;
}

function withoutImpressionSection(document: ReportDocument): ReportDocument {
  return {
    sections: document.sections.filter((section) => section.title.toUpperCase() !== "IMPRESSION"),
  };
}

/** Findings stay exactly as prepared; the Impression section states, in the
 * document itself, that no Impression could be generated. Returns null when
 * there are no Findings to carry the notice. */
function withImpressionUnavailableNotice(document: ReportDocument | null): ReportDocument | null {
  if (!document || !document.sections.some((section) => section.blocks.length > 0)) return null;
  return {
    sections: [
      ...withoutImpressionSection(document).sections,
      { title: "IMPRESSION", blocks: [{ kind: "notice", text: IMPRESSION_UNAVAILABLE_NOTICE }] },
    ],
  };
}

/** Parse persisted final report text. Unknown uppercase headings remain
 * first-class sections; no anatomy or clinical vocabulary is assumed. */
export function reportDocumentFromText(text: string): ReportDocument {
  const lines = text.replaceAll("\r\n", "\n").split("\n");
  const statusIndex = lines.findIndex((line) => line.trim());
  if (
    statusIndex >= 0 &&
    /^(?:FINAL REPORT|DRAFT REPORT\s*[—-]\s*NOT SIGNED|DRAFT\s*\/\s*NOT SIGNED)$/i.test(
      lines[statusIndex].trim(),
    )
  ) {
    lines.splice(statusIndex, 1);
  }

  const sections: ReportDocumentSection[] = [];
  let current: ReportDocumentSection | null = null;
  const headingPattern = /^([A-Z][A-Z0-9 /&(),.'-]{1,60}):$/;

  for (const sourceLine of lines) {
    const line = sourceLine.trim();
    const heading = line.match(headingPattern);
    if (heading) {
      current = { title: heading[1], blocks: [] };
      sections.push(current);
      continue;
    }
    if (!line) continue;
    if (!current) {
      current = { title: "REPORT", blocks: [] };
      sections.push(current);
    }
    const numbered = line.match(/^(\d+)[.)]\s+(.+)$/);
    if (numbered) {
      current.blocks.push({
        kind: "numbered",
        number: numbered[1],
        text: numbered[2],
        emphasis: "standard",
      });
      continue;
    }
    if (line === GPT_IMPRESSION_DISCLOSURE) {
      current.blocks.push({ kind: "disclosure", text: line });
      continue;
    }
    const labeled = line.match(/^([A-Za-z][A-Za-z0-9 /&(),.'-]{1,60}):\s+(.+)$/);
    if (labeled) {
      current.blocks.push({
        kind: "labeled",
        label: labeled[1],
        items: [{ text: labeled[2], emphasis: "standard" }],
      });
      continue;
    }
    current.blocks.push({ kind: "paragraph", text: line, emphasis: "standard" });
  }

  return { sections };
}

function safeStudyId(studyId: string): string {
  const raw = studyId.split("::")[0] || studyId;
  return raw.replaceAll(/[^A-Za-z0-9._-]/g, "_").slice(-80) || "case";
}

/** Resolve product state once, before any output renderer is selected. Fresh
 * drafts run through the same report and Impression builders as the editor. */
export async function makeDownloadableReport(
  studyId: string,
  report: SavedReportForDownload,
  generated?: GeneratedReportForDownload | null,
  options?: ReportDownloadOptions,
  dependencies: ReportPreparationDependencies = { streamImpression },
): Promise<DownloadableReport | null> {
  const noteFailure = options?.onImpressionFailure === "note";
  const finalText = report.final_report_text?.trim();
  let document: ReportDocument | null;
  let impressionUnavailable = false;
  // In note mode, an Impression failure downgrades to the in-document notice
  // built from `fallback` — unless there is nothing to carry it, or the caller
  // aborted, in which case the original error stands.
  const noteOrRethrow = (error: unknown, fallback: ReportDocument | null): ReportDocument => {
    if (!noteFailure || (error instanceof DOMException && error.name === "AbortError")) throw error;
    const noted = withImpressionUnavailableNotice(fallback);
    if (!noted) throw error;
    impressionUnavailable = true;
    return noted;
  };

  if (finalText) {
    document = reportDocumentFromText(finalText);
  } else {
    const savedDocument = editorStateToReportDocument(report.structured_editor_state);
    if (savedDocument) {
      if (hasLegacyTemplateImpression(report.structured_editor_state)) {
        try {
          document = await replaceLegacyTemplateImpression(
            report.structured_editor_state,
            dependencies.streamImpression,
          );
        } catch (error) {
          // The retired canned Impression must never print: the fallback is
          // the saved Findings with the notice, not the saved Impression.
          document = noteOrRethrow(error, withoutImpressionSection(savedDocument));
        }
      } else if (!hasImpression(savedDocument)) {
        document = noteOrRethrow(
          new Error("The saved draft has no Impression. Open the case and complete it first."),
          savedDocument,
        );
      } else {
        document = savedDocument;
      }
    } else if (generated) {
      requireReadyWording(generated.meta?.wording);
      try {
        document = await generatedReportToDocument(generated, dependencies.streamImpression);
      } catch (error) {
        document = noteOrRethrow(
          error,
          editorStateToReportDocument(generatedEditorState(generated)),
        );
      }
    } else {
      document = null;
    }
  }
  if (!document || !document.sections.some((section) => section.blocks.length > 0)) return null;

  const signed = Boolean(report.is_report_signed && finalText);
  const hasAccession = options && Object.hasOwn(options, "accessionNumber");
  const normalizedAccession = options?.accessionNumber?.replaceAll(/\s+/g, " ").trim() || null;

  return {
    filename: `report-${safeStudyId(studyId)}${signed ? "" : "-draft"}.pdf`,
    signed,
    document,
    caseIdentifier: safeStudyId(studyId),
    ...(hasAccession ? { accessionNumber: normalizedAccession } : {}),
    ...(impressionUnavailable ? { impressionUnavailable } : {}),
  };
}
